import React, { useState, useEffect } from 'react';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Routes, Route } from 'react-router-dom';
import { AccountContext } from './App';
import HomePage from './containers/HomePage/HomePage';
import RequestPage from './containers/RequestPage/RequestPage';
import WorkflowResponseForm from './containers/WorkflowResponseForm';
import Layout from './components/Layout';
import UnauthenticatedView from './components/UnauthenticatedView';
import IdleTimeoutModal from './components/IdleTimeoutModal';
import { refreshTokenOrLogin } from './utils/tokenRefresh';

import { RequestTypesProvider } from './contexts/RequestTypesContext';
import { RequestsProvider } from './contexts/RequestsContext';
import { LayoutProvider } from './contexts/LayoutContext';
import { WorkflowProvider } from './contexts/WorkflowContext';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import Workflows from './containers/WorkflowsPage/Workflows';
import { IDLE_TIMEOUT_MS } from './constants/app';

const MainContent = () => {
  const { instance, inProgress, accounts } = useMsal();
  const [activeAccount, setActiveAccount] = useState(
    instance.getActiveAccount()
  );
  const [showIdleModal, setShowIdleModal] = useState(false);

  // Idle timeout detection - 30 seconds
  const { resetTimer } = useIdleTimeout({
    enabled: !!activeAccount, // Only enable when user is authenticated
    onIdle: () => {
      setShowIdleModal(true);
    },
    timeoutMs: IDLE_TIMEOUT_MS,
  });

  useEffect(() => {
    // Update active account whenever MSAL accounts change
    const currentAccount = instance.getActiveAccount();
    setActiveAccount(currentAccount);
  }, [instance, accounts]);

  const handleRedirect = () => {
    const loginRequest = {
      scopes: ['User.Read'],
    };

    instance.loginRedirect(loginRequest);
  };

  const handleContinueSession = async () => {
    const success = await refreshTokenOrLogin(instance);

    if (success) {
      setShowIdleModal(false);
      resetTimer();
    }
    // If unsuccessful, user will be redirected to login by the utility
  };

  const handleLogout = () => {
    setShowIdleModal(false);

    instance.logoutRedirect({
      postLogoutRedirectUri: '/',
    });
  };

  const handleAutoLogout = () => {
    handleLogout();
  };

  // Show loading state while MSAL is performing operations
  if (inProgress !== 'none') {
    return (
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          height: '100vh',
          justifyContent: 'center',
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <AccountContext.Provider value={{ activeAccount, setActiveAccount }}>
      <div className="App">
        <AuthenticatedTemplate>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <RequestTypesProvider>
              <RequestsProvider>
                <WorkflowProvider>
                  <LayoutProvider>
                    <Layout>
                      <Routes>
                        <Route index element={<HomePage />} />
                        <Route
                          path="request/:requestTypeId"
                          element={<RequestPage />}
                        />
                        <Route path="workflows" element={<Workflows />} />
                        <Route
                          path="workflows/:workflowId"
                          element={<WorkflowResponseForm />}
                        />
                      </Routes>
                    </Layout>
                  </LayoutProvider>
                </WorkflowProvider>
              </RequestsProvider>
            </RequestTypesProvider>
          </LocalizationProvider>
          {/* Idle timeout modal */}
          <IdleTimeoutModal
            open={showIdleModal}
            onContinue={handleContinueSession}
            onLogout={handleLogout}
            onCountdownEnd={handleAutoLogout}
          />
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <UnauthenticatedView onAuthenticate={handleRedirect} />
        </UnauthenticatedTemplate>
      </div>
    </AccountContext.Provider>
  );
};

export default MainContent;
